// packages/web-ui/src/components/SkipLink/SkipLink.tsx
//
// W2-S10b Task 4.1 (AC #4) — first-focusable skip-link for keyboard users.
//
// The link is `sr-only` until focused, then visible at top-left with high-
// contrast styling; activating it moves focus to the `<main id="main-content">`
// landmark (rendered by `<TabContentArea>`).
//
// AC #4 (W2-S10): "<a href="#main-content">Skip to content</a> is the **first
// focusable element on every page** — visually hidden until focused, then
// visible at top-left with high-contrast styling; activating it moves focus
// to the `<main id="main-content">` landmark."

import type { JSX } from 'react';
import { Z_INDEX } from '../../AppShell/responsive-tokens.js';

export interface SkipLinkProps {
  /** Target id (default: `main-content`). */
  targetId?: string;
  /** Visible label (default: `Skip to main content`). */
  label?: string;
  /** Optional test-id override. */
  testId?: string;
}

/**
 * The skip-link is rendered as the very first child of `<AppShell>`. Browsers
 * make it the first tab-stop on the page automatically. When focused it pops
 * into view at top-left so sighted keyboard users see what's happening before
 * activating it.
 */
export function SkipLink({
  targetId = 'main-content',
  label = 'Skip to main content',
  testId = 'skip-link',
}: SkipLinkProps): JSX.Element {
  return (
    <a
      href={`#${targetId}`}
      data-testid={testId}
      // Tailwind's `sr-only` collapses to 1px until focused; the
      // `focus:not-sr-only` + positioning utilities flip it back on.
      className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:rounded focus:bg-cream-100 focus:px-3 focus:py-1 focus:text-fg focus:shadow"
      style={{ zIndex: Z_INDEX.toast }}
    >
      {label}
    </a>
  );
}
